import { TimeSeriesTableHelpTabId } from './timeSeriesTableHelp.helper'

/**
 * Where the demo's pointer is drawn. Each value matches a `data-demo-anchor` attribute on the
 * element the scripted gesture is acting on.
 */
export type TimeSeriesTableHelpDemoAnchor =
  | `cell-${number}`
  | `handle-${number}`
  | `checkbox-${number}`
  | 'copy-button'
  | 'copy-menu'
  | 'mw-field'
  | 'calculate-button'

/**
 * What one frame of the demo shows. The demo only plays frames back — nothing in it reacts to the
 * user, so there is no editing or selection state to track beyond what a frame states.
 */
export type TimeSeriesTableHelpDemoState = {
  values: (number | null)[]
  selectedRows: number[]
  mwInput: string
  menuOpen: boolean
  hoverRow: number | null
  dragFrom: number | null
  dragTo: number | null
  pointer: TimeSeriesTableHelpDemoAnchor | null
  pressed: boolean
}

export type TimeSeriesTableHelpDemoFrame = {
  durationMs: number
  state: Partial<TimeSeriesTableHelpDemoState>
}

/**
 * Six fixed hours with fixed numbers: the demo illustrates the gestures and is never wired to the
 * real table's data or state.
 */
export const DEMO_TIMES = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00']
export const DEMO_OUTCOME_VALUES = [12.4, 14.1, 15.8, 16.2, 15.0, 13.6]
export const DEMO_INITIAL_VALUES: (number | null)[] = [9.5, 22.5, 11.0, 14.0, 8.5, 12.0]

export const INITIAL_DEMO_STATE: TimeSeriesTableHelpDemoState = {
  values: DEMO_INITIAL_VALUES,
  selectedRows: [],
  mwInput: '',
  menuOpen: false,
  hoverRow: null,
  dragFrom: null,
  dragTo: null,
  pointer: null,
  pressed: false,
}

const FILL_SOURCE_ROW = 1
const CALCULATE_MW_OFFSET = 10

export function formatDemoValue(value: number | null): string {
  return value === null ? '' : value.toFixed(1)
}

function withValues(rows: number[], value: number | null): (number | null)[] {
  return DEMO_INITIAL_VALUES.map((current, index) => (rows.includes(index) ? value : current))
}

function withOutcomeValues(rows: number[], offset = 0): (number | null)[] {
  return DEMO_INITIAL_VALUES.map((current, index) =>
    rows.includes(index) ? DEMO_OUTCOME_VALUES[index] + offset : current
  )
}

/**
 * One scripted loop per tab: where the pointer goes, when the button is held, and what the table
 * looks like as a result. Frame durations come from the design prototype.
 *
 * A frame states only what it changes, so a frame is read on top of the frames before it — which
 * is why the first frame of every script restates the whole of `INITIAL_DEMO_STATE`, and why the
 * loop starts clean when it wraps around.
 */
const DEMO_SCRIPTS: Record<TimeSeriesTableHelpTabId, TimeSeriesTableHelpDemoFrame[]> = {
  multiEdit: [
    { durationMs: 900, state: { ...INITIAL_DEMO_STATE, pointer: 'cell-0' } },
    { durationMs: 450, state: { pressed: true, selectedRows: [0] } },
    { durationMs: 500, state: { pressed: false, pointer: 'cell-2' } },
    { durationMs: 450, state: { pressed: true, selectedRows: [2] } },
    { durationMs: 800, state: { pointer: 'cell-4', selectedRows: [2, 3, 4] } },
    { durationMs: 400, state: { pressed: false, pointer: 'cell-2' } },
    { durationMs: 500, state: { values: withValues([2, 3, 4], 2) } },
    { durationMs: 1800, state: { values: withValues([2, 3, 4], 25) } },
  ],
  fillHandle: [
    { durationMs: 900, state: { ...INITIAL_DEMO_STATE, hoverRow: FILL_SOURCE_ROW, pointer: 'handle-1' } },
    { durationMs: 500, state: { pressed: true, dragFrom: FILL_SOURCE_ROW, dragTo: FILL_SOURCE_ROW } },
    { durationMs: 350, state: { pointer: 'cell-2', dragTo: 2 } },
    { durationMs: 350, state: { pointer: 'cell-3', dragTo: 3 } },
    { durationMs: 450, state: { pointer: 'cell-4', dragTo: 4 } },
    {
      durationMs: 1800,
      state: {
        pressed: false,
        dragFrom: null,
        dragTo: null,
        values: withValues([2, 3, 4], DEMO_INITIAL_VALUES[FILL_SOURCE_ROW]),
        selectedRows: [1, 2, 3, 4],
      },
    },
  ],
  copyFrom: [
    { durationMs: 900, state: { ...INITIAL_DEMO_STATE, selectedRows: [2, 3, 4], pointer: 'copy-button' } },
    { durationMs: 600, state: { pressed: true } },
    { durationMs: 900, state: { pressed: false, menuOpen: true, pointer: 'copy-menu' } },
    { durationMs: 1800, state: { menuOpen: false, pointer: null, values: withOutcomeValues([2, 3, 4]) } },
  ],
  calculate: [
    { durationMs: 800, state: { ...INITIAL_DEMO_STATE, pointer: 'checkbox-1' } },
    { durationMs: 450, state: { pressed: true, selectedRows: [1] } },
    { durationMs: 350, state: { pressed: false, pointer: 'checkbox-2' } },
    { durationMs: 450, state: { pressed: true, selectedRows: [1, 2] } },
    { durationMs: 350, state: { pressed: false, pointer: 'checkbox-3' } },
    { durationMs: 450, state: { pressed: true, selectedRows: [1, 2, 3] } },
    { durationMs: 500, state: { pressed: false, pointer: 'mw-field', mwInput: '1' } },
    { durationMs: 500, state: { mwInput: String(CALCULATE_MW_OFFSET) } },
    { durationMs: 500, state: { pointer: 'calculate-button' } },
    { durationMs: 350, state: { pressed: true } },
    {
      durationMs: 1800,
      state: {
        pressed: false,
        values: withOutcomeValues([1, 2, 3], CALCULATE_MW_OFFSET),
        selectedRows: [],
        mwInput: '',
      },
    },
  ],
}

/**
 * What the demo shows at `frame`, counted from the start of the loop. The frame number is allowed
 * to run past the end of the script — it wraps, so the caller only ever has to count upwards.
 */
export function getDemoStateAtFrame(tabId: TimeSeriesTableHelpTabId, frame: number): TimeSeriesTableHelpDemoState {
  const script = DEMO_SCRIPTS[tabId]
  return script
    .slice(0, (frame % script.length) + 1)
    .reduce((state, played) => ({ ...state, ...played.state }), INITIAL_DEMO_STATE)
}

export function getDemoFrameDurationMs(tabId: TimeSeriesTableHelpTabId, frame: number): number {
  const script = DEMO_SCRIPTS[tabId]
  return script[frame % script.length].durationMs
}

/**
 * The state the loop ends on, with the pointer taken away — what a reader who has asked for
 * reduced motion is shown instead of the animation.
 */
export function getFinalDemoState(tabId: TimeSeriesTableHelpTabId): TimeSeriesTableHelpDemoState {
  const final = getDemoStateAtFrame(tabId, DEMO_SCRIPTS[tabId].length - 1)
  return { ...final, pointer: null, pressed: false }
}
